import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FilePlus2, Star } from "lucide-react";
import { toast } from "sonner";
import { InvoiceList } from "@/components/crm/InvoiceDialog";
import { Timeline } from "@/components/crm/EstimatePanel";
import { api, errorMessage } from "@/lib/api";
import { StageBadge, btnGold, btnLine, days } from "@/lib/crm";
import { money } from "@/lib/pricing";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => { toast.error(errorMessage(e)); nav("/customers"); }), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!p) return <div className="h-40 animate-pulse rounded-2xl bg-slate-900/30" />;
  const active = p.estimates.find((e) => e.active) || p.estimates[p.estimates.length - 1];
  const markActive = async (qid) => {
    try { setP((await api.post(`/projects/${id}/active`, { quote_id: qid })).data); toast.success("Active estimate set"); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-8" data-testid="project-detail-page">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to={`/customers/${p.customer_id}`} data-testid="project-customer-link" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300"><ArrowLeft className="h-3.5 w-3.5" /> {p.customer_name}</Link>
          <h1 data-testid="project-detail-name" className="mt-2 font-display text-4xl font-semibold text-slate-50 sm:text-5xl">{p.name}</h1>
          <p className="mt-2 text-sm text-slate-400">{p.site_address || "No site address"} · {p.showroom_name} · {p.rep_name}</p>
          {active && <p className="mt-2 flex items-center gap-2 text-sm text-slate-300" data-testid="project-status">Status <StageBadge stage={active.stage} /> · {days(active.days_in_stage)} in stage</p>}
        </div>
        <button data-testid="project-new-estimate-button" onClick={() => nav(`/quotes/new?project=${p.id}`)} className={btnGold}><FilePlus2 className="h-4 w-4" /> New estimate</button>
      </div>
      <section className="rounded-2xl border border-slate-800 p-6" data-testid="project-estimates">
        <h2 className="mb-3 font-display text-2xl text-slate-50">Estimates</h2>
        <div className="divide-y divide-slate-800/60">
          {p.estimates.map((e, i) => (
            <div key={e.id} data-testid={`project-estimate-${i}`} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <Link to={`/quotes/${e.id}`} className="flex items-center gap-3 hover:text-amber-600">
                <span className="font-mono-num text-sm text-slate-200">{e.number}</span><span className="text-xs text-slate-500">v{e.version}</span>
                <StageBadge stage={e.stage} testId={`project-estimate-stage-${i}`} />
                {e.active && <span data-testid={`project-estimate-active-${i}`} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-amber-600"><Star className="h-3 w-3 fill-current" /> Active</span>}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{e.days_sent_to_paid != null ? `Sent→${e.paid_done ? "Paid" : "now"}: ${days(e.days_sent_to_paid)}` : ""}</span>
                <span className="font-mono-num text-sm font-semibold text-slate-100">{money(e.total)}</span>
                {!e.active && <button data-testid={`project-estimate-make-active-${i}`} onClick={() => markActive(e.id)} className={btnLine}>Make active</button>}
              </div>
            </div>
          ))}
          {p.estimates.length === 0 && <p className="py-6 text-sm text-slate-500">No estimates yet.</p>}
        </div>
      </section>
      {active && <section className="rounded-2xl border border-slate-800 p-6"><h2 className="mb-3 font-display text-2xl text-slate-50">Timeline · {active.number}</h2><Timeline items={active.timeline} /></section>}
      <section className="rounded-2xl border border-slate-800 p-6" data-testid="project-invoices">
        <h2 className="mb-3 font-display text-2xl text-slate-50">Invoices</h2>
        <InvoiceList invoices={p.invoices} onChanged={load} prefix="project-invoice" />
      </section>
    </div>
  );
}
